<template>
  <CollapsableSection title="Custom Attributes">
    <ListEditor
      :buttonText="buttonText"
      component="CustomAttributeFields"
      :disableAddListItem="disableAddListItem"
      :handleAddListItem="handleAddAttribute"
      :handleChangeListItem="handleChangeListItem"
      :handleRemoveListItem="handleRemoveAttribute"
      :headerText="headerText"
      :listItems="item.attributes"
    />
  </CollapsableSection>
</template>
<script>
import { cloneDeep } from 'lodash';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import CollapsableSection from 'client/components/ContentSection/CollapsableSection.vue';
import ListEditor from 'client/components/FormValidation/ListEditor.vue';

export default {
  name: 'CustomAttributes',

  components: {
    CollapsableSection,
    ListEditor,
  },

  inject: ['$validator'],

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  data() {
    return {
      addingAttribute: false,
      buttonText: 'add new custom attribute',
      headerText: 'Create and store custom attributes for this user. Attribute field names must be unique.',
    };
  },

  computed: {
    /**
     * Disable the add button when adding a new attribute until the new
     * attribute has both a name and a value, or it is removed.
     */
    disableAddListItem() {
      // There may not be an item until it has been fetched from the server. So
      // the lazy evaluation to check for a name and value on the first
      // attribute is required here to avoid failing in that case.
      const { name, value } = (this.item && this.item.attributes && this.item.attributes[0]) || {};

      if (this.addingAttribute && (!name || !value)) {
        return true;
      }

      return false;
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  methods: {
    /**
     * When creating a new attribute a temporary id is added to the attribute
     * object which is used in the ListEditor's v-for loop as a unique key and
     * also in the CustomAttributeField to provide a unique name for each input
     * rendered. It was noticed that the server ignores this field when saving
     * the attributes so it can safely be inserted temporarily on the client
     * side. If the server is changed to validate the array of attributes then
     * the client side code will have to map the attribute array of objects to
     * a hash generating a unique value for the key. This is deemed more work
     * at the current time and should only be addressed if/when server side
     * attribute validation is introduced.
     */
    handleAddAttribute() {
      const attributes = cloneDeep(this.item.attributes);
      attributes.splice(0, 0, { tempId: String(Date.now()), name: '', value: '' });
      this.setItemField({ key: 'attributes', value: attributes });
      this.addingAttribute = true;
    },

    handleChangeListItem(index, listItem) {
      const attributes = cloneDeep(this.item.attributes);
      attributes[index] = { ...attributes[index], ...listItem };
      this.setItemField({ key: 'attributes', value: attributes });

      if (this.addingAttribute && index === 0 && !this.disableAddListItem) {
        this.addingAttribute = false;
      }
    },

    handleRemoveAttribute(index) {
      const attributes = cloneDeep(this.item.attributes);
      attributes.splice(index, 1);
      this.setItemField({ key: 'attributes', value: attributes });

      if (this.addingAttribute && index === 0) {
        this.addingAttribute = false;
      }
    },
  },
};
</script>
